<template>
  <div class="v-uploader">
    <media-library-collection
      ref="mediaLibraryCollection"
      :afterUpload="onAfterUpload"
      :initial-value="media"
      :route-prefix="routePrefix"
      :translations="translations"
      :max-items="maxItems"
      :multiple="multiple"
      :upload-domain="domain"
      :validation-rules="validationRules"
      :with-credentials="true"
      :headers="headers"
      name="downloads"
    />

    <div v-if="enableLibrary && canSelectFromLibrary" class="v-uploader-library">
      <div class="v-uploader-library-divider">
        <span>{{ $t("uploader.or") }}</span>
      </div>
      <button
        type="button"
        class="btn btn-outline-primary btn-sm w-100 v-uploader-library-btn"
        :disabled="isStagingFromLibrary"
        @click="openLibrary"
      >
        <v-icon icon="picture" size="sm" space="me-8" />
        {{ isStagingFromLibrary ? $t("uploader.choosingFromLibrary") : $t("uploader.chooseFromLibrary") }}
      </button>
    </div>

    <cms-media-picker
      v-if="enableLibrary"
      v-model="showMediaPicker"
      :filter-type="libraryFilterType"
      :multiple="multiple"
      :max-selection="libraryMaxSelection"
      @selected="handleLibrarySelected"
    />
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { MediaLibraryCollection } from "@spatie/media-library-pro-vue3-collection";
import { useToast } from "vue-toastification";
import { getHummanSize } from "@/helpers/General";
import { useAuthStore } from "@/stores/auth";
import api from "@/api";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import CmsMediaPicker from "@/components/cms/CmsMediaPicker.vue";

export default defineComponent({
  name: "VUploader",
  props: {
    headers: {
      required: false,
      type: Object,
    },
    name: {
      required: false,
      type: String,
    },
    initialValue: {
      required: false,
      type: [Array, Object],
    },
    validationErrors: {
      default: () => ({}),
      type: [Object, Array],
    },
    routePrefix: {
      required: false,
      type: String,
      default: "api",
    },
    validationRules: {
      required: false,
      type: Object,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    sortable: {
      default: true,
      type: Boolean,
    },
    maxItems: {
      required: false,
      type: Number,
    },
    maxSizeForPreviewInBytes: {
      required: false,
      type: Number,
    },
    vapor: {
      required: false,
      type: Boolean,
    },
    vaporSignedStorageUrl: {
      required: false,
      type: String,
    },
    uploadDomain: {
      required: false,
      type: String,
    },
    withCredentials: {
      required: false,
      type: Boolean,
    },
    fileTypeHelpText: {
      required: false,
      type: String,
    },
    beforeUpload: {
      type: Function,
    },
    afterUpload: {
      type: Function,
    },
    enableLibrary: {
      type: Boolean,
      default: true,
    },
    libraryFilterType: {
      type: String,
      default: "",
    },
  },
  emits: [
    "removed",
    "change",
    "is-ready-to-submit-change",
    "has-uploads-in-progress-change",
    "isReadyToSubmitChange",
    "hasUploadsInProgressChange",
  ],
  components: {
    MediaLibraryCollection,
    VIcon,
    CmsMediaPicker,
  },
  data() {
    return {
      mediaLibrary: null,
      media: this.initialValue,
      showMediaPicker: false,
      isStagingFromLibrary: false,
      libraryItemCount: 0,
    };
  },
  computed: {
    domain() {
      return import.meta.env.VITE_APP_API_URL;
    },
    translations() {
      return {
        fileTypeNotAllowed: this.$t("uploader.fileTypeNotAllowed"),
        tooLarge: this.$t("uploader.tooLarge"),
        tooSmall: this.$t("uploader.tooSmall"),
        tryAgain: this.$t("uploader.tryAgain"),
        somethingWentWrong: this.$t("uploader.somethingWentWrong"),
        selectOrDrag: this.$t("uploader.selectOrDrag"),
        selectOrDragMax: this.$t("uploader.selectOrDrag"),
        file: {
          singular: this.$t("uploader.file.singular"),
          plural: this.$t("uploader.file.plural"),
        },
        anyImage: this.$t("uploader.anyImage"),
        anyVideo: this.$t("uploader.anyVideo"),
        goBack: this.$t("uploader.goBack"),
        dropFile: this.$t("uploader.dropFile"),
        dragHere: this.$t("uploader.dragHere"),
        remove: this.$t("uploader.remove"),
        download: this.$t("uploader.download"),
      };
    },
    effectiveMaxItems() {
      if (!this.multiple) {
        return 1;
      }
      return this.maxItems || 0;
    },
    canSelectFromLibrary() {
      if (!this.effectiveMaxItems) {
        return true;
      }
      return this.libraryItemCount < this.effectiveMaxItems;
    },
    libraryMaxSelection() {
      if (!this.multiple) {
        return 1;
      }
      if (!this.effectiveMaxItems) {
        return undefined;
      }
      return Math.max(this.effectiveMaxItems - this.libraryItemCount, 1);
    },
  },
  watch: {
    initialValue: {
      handler(value) {
        this.media = value;
      },
      deep: true,
    },
  },
  mounted() {
    this.setup();
  },
  methods: {
    getHummanSize,
    setup() {
      this.mediaLibrary = this.$refs.mediaLibraryCollection.mediaLibrary;

      if (this.mediaLibrary?.subscribe) {
        this.mediaLibrary.subscribe(() => {
          this.syncLibraryItemCount();
        });
      }
      this.syncLibraryItemCount();

      const iconButton = document.querySelector(".media-library-button-info");
      if (iconButton) {
        iconButton.innerHTML = iconButton.innerHTML ? '<span class="icon icon-export"/>' : "";
      }
    },
    syncLibraryItemCount() {
      this.libraryItemCount = this.mediaLibrary?.state?.media?.length || 0;
    },
    onAfterUpload(payload) {
      if (typeof this.afterUpload === "function") {
        this.afterUpload(payload);
      }
      this.syncLibraryItemCount();
    },
    openLibrary() {
      const authStore = useAuthStore();
      if (!authStore.agency?.slug) {
        useToast().error(this.$t("uploader.chooseFromLibraryFailed"));
        return;
      }
      this.showMediaPicker = true;
    },
    unwrapStagedMedia(response) {
      const candidates = [response, response?.data, response?.data?.data];
      for (const candidate of candidates) {
        if (candidate?.uuid) {
          return candidate;
        }
      }
      return response;
    },
    addStagedMedia(staged) {
      if (!this.mediaLibrary || !staged?.uuid) {
        return;
      }

      const previewUrl = staged.preview_url || staged.previewUrl || staged.url || staged.original_url || "";
      const originalUrl = staged.original_url || staged.originalUrl || staged.url || previewUrl;

      this.mediaLibrary.changeState((state) => {
        state.media.push({
          attributes: {
            uuid: staged.uuid,
            name: staged.name || "media",
            order: state.media.length,
            custom_properties: {},
            preview_url: previewUrl || null,
            original_url: originalUrl || null,
            extension: staged.extension,
            size: staged.size,
          },
          client_preview: previewUrl || originalUrl || null,
          clientValidationErrors: [],
          upload: {
            uploadProgress: 100,
            hasFinishedUploading: true,
            hadErrorWhileUploading: false,
            errorMessage: null,
            cancelTokenSource: null,
          },
        });
      });
    },
    async handleLibrarySelected(media) {
      const items = Array.isArray(media) ? media : [media];
      const authStore = useAuthStore();
      const agencySlug = authStore.agency?.slug;
      const toast = useToast();

      if (!agencySlug || items.length === 0) {
        return;
      }

      this.isStagingFromLibrary = true;

      try {
        for (const item of items) {
          const currentCount = this.mediaLibrary?.state?.media?.length || 0;
          if (this.effectiveMaxItems && currentCount >= this.effectiveMaxItems) {
            break;
          }

          const response = await api.media.stage(
            agencySlug,
            {
              media_id: item.id,
              name: item.name || item.fileName || item.file_name,
            },
            this.headers || {},
          );
          const staged = this.unwrapStagedMedia(response);
          this.addStagedMedia(staged);
          this.onAfterUpload({ success: true, uuid: staged.uuid });
        }
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message || error?.message || this.$t("uploader.chooseFromLibraryFailed"));
      } finally {
        this.isStagingFromLibrary = false;
        this.syncLibraryItemCount();
      }
    },
    remove(object) {
      this.mediaLibrary.removeMedia(object.uuid);

      this.$emit("removed", null);
      this.syncLibraryItemCount();
    },
    handleSortEnd(event) {
      this.$refs.mediaLibraryCollection.setOrder(
        Array.from(event.from.children || []).map((element) => element.getAttribute("data-media-library-uuid")),
        false,
      );
    },
  },
});
</script>

<style lang="scss" scoped>
.v-uploader-library {
  margin-top: 0.75rem;
}

.v-uploader-library-divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: #6c757d;
  font-size: 0.8rem;
  margin-bottom: 0.75rem;

  &::before,
  &::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid #dee2e6;
  }

  span {
    padding: 0 0.75rem;
  }
}

.v-uploader-library-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
